import { Entity, stringifyEntityRef } from '@backstage/catalog-model';

import { ProposedOwnershipRepo } from '../../types';
import { OwnershipEntity } from './types';

/**
 * Convert a Backstage Entity to an OwnershipEntity for use with ManageOwnershipDialog.
 * Handles different entity types including repository and jira-team components.
 */
export function entityToOwnershipEntity(entity: Entity): OwnershipEntity {
  const annotations = entity.metadata.annotations || {};

  // Build entity link
  const entityRef = stringifyEntityRef(entity);
  const entityLink = `/catalog/${
    entity.metadata.namespace || 'default'
  }/${entity.kind.toLowerCase()}/${entity.metadata.name}`;

  let kind = entity.kind;
  if (entity.kind === 'Component') {
    const componentType = entity.spec?.type as string | undefined;
    if (componentType === 'jira-team') {
      kind = 'jira-team';
    }
  } else if (entity.kind === 'Resource') {
    const resourceType = entity.spec?.type as string | undefined;
    if (resourceType === 'subscription') {
      kind = 'subscription';
    }
  }

  return {
    id: entity.metadata.uid || entity.metadata.name,
    name: entity.metadata.name,
    kind,
    entityRef,
    displayName: entity.metadata.title || entity.metadata.name,
    currentOwner: entity.spec?.owner as string | undefined,
    proposedOwner: annotations['tomtom.com/proposed-owner'],
    annotations,
    entityLink,
  };
}

/**
 * Convert a ProposedOwnershipRepo to an OwnershipEntity for use with ManageOwnershipDialog.
 * Used primarily for the action-required page.
 */
export function proposedRepoToOwnershipEntity(
  repo: ProposedOwnershipRepo,
): OwnershipEntity {
  // Build entity link from entityRef
  const parts = repo.entityRef.split('/');
  const namespace =
    parts.length > 1 ? parts[0].split(':')[1] || 'default' : 'default';
  const name = parts.length > 1 ? parts[1] : parts[0];
  const entityLink = `/catalog/${namespace}/repository/${name}`;

  return {
    id: repo.id,
    name: repo.repositoryName,
    kind: 'Repository',
    entityRef: repo.entityRef,
    displayName: repo.entityName || repo.repositoryName,
    proposedOwner: repo.teamEntityRef,
    proposedOwnerDisplayName: repo.teamRecommendation,
    annotations: {
      'github.com/project-slug': repo.repository,
    },
    entityLink,
  };
}
